import { TrashIcon } from '@heroicons/react/outline';
import { Button } from 'components/atoms/Button';
import { Modal } from 'components/atoms/Modal';
import CustomLoader from 'components/molecules/CustomLoader.component';
import PricingPlans from 'components/organisms/PricingPlans';
import AccountDeletion from 'components/organisms/SettingsViews/AccountDetailsView/AccountDeletion';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import logout from 'hooks/Authentication/useLogout';
import {
  useGroupSubscriptionPlans,
  useRedirectToCustomerBillingPortal,
  useSubscriptionPlan,
} from 'hooks/Subscriptions';
import { useEffect, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { IUser } from 'types/users.types';
import { WEBAPP_PAYWALL_CLICKEDOPENDELETEACCOUNTMODAL } from 'utils/amplitude/events/global.amplitude';
import {
  isSubscriptionNoWebAccessPlan,
  isSubscriptionProMax12Plan,
} from 'utils/subscriptions.utils';

const SubscriptionPlans: FC = () => {
  const { t } = useTranslation();

  const location = useLocation();

  const navigate = useNavigate();

  const { data: user, isLoading: isLoadingUser } = useGetCurrentCustomerData();

  const { isLoading: isLoadingSubscriptionPlans } = useGroupSubscriptionPlans();

  const { mutate } = useRedirectToCustomerBillingPortal();

  const planInternalName = useSubscriptionPlan()?.planInternalName;

  const isPROMaxUser = isSubscriptionProMax12Plan(planInternalName);

  useEffect(() => {
    if (isPROMaxUser) {
      mutate();
    }
  }, [isPROMaxUser]);

  if (isLoadingUser || isLoadingSubscriptionPlans || isPROMaxUser)
    return <CustomLoader className="h-screen" />;

  return (
    <Modal
      name="subscription plan"
      color="taxes"
      disableFocusTrap
      trigger={() => null}
      onBlur={() => null}
      render={() => (
        <div className="p-12">
          <PricingPlans />
          <AccountDeletion
            user={user as IUser}
            Trigger={({ api, isLoading }: any) => (
              <Button
                type="button"
                color="primary"
                structure="text"
                icon={TrashIcon}
                onClick={() => api.openModal()}
                tracingEvent={WEBAPP_PAYWALL_CLICKEDOPENDELETEACCOUNTMODAL}
                loading={isLoading}
                className="ml-3 mt-5 opacity-50"
              >
                {t('settings.controllers.deleteAccount')}
              </Button>
            )}
          />
        </div>
      )}
      defaultOpen
      isFocusTrapActive={false}
      onClose={() => {
        if (isSubscriptionNoWebAccessPlan(planInternalName)) {
          return logout();
        }
        if ((location.state as any)?.from === 'paymentFailed') {
          return navigate('/');
        }

        return navigate(-1);
      }}
      width="7xl"
      dialogPanalClassName="!p-0 !bg-taxes-100"
    />
  );
};

export default SubscriptionPlans;
